import { useState } from 'react';

import { faPlus, faTimeline } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Book, LoaderCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SelectChronologyModal } from './SelectChronologyModal';
import ShowSupportingTexts from './ShowSupportingText';
import { useCreateChronologyFromThread } from '@/api/mutations/useCreateChronologyFromThread';
import { Button } from '@/components/atoms/new-button';
import { NewChip } from '@/components/atoms/NewChip';
import SkeletonOrComponent from '@/components/molecules/SkeletonOrComponent';
import FactDropdown from '@/components/organisms/FactDropdown';
import { useStore } from '@/store';
import { ConfidenceLevelObject, Fact, ThreadMessage } from '@/types';

export interface SupportingEvidenceProps {
  loading?: boolean;
  animate: boolean;
  answerMessage?: ThreadMessage;
  confidenceLevel: ConfidenceLevelObject;
  facts: Fact[];
  showAllChunks: boolean;
  setShowAllChunks: (bool: boolean) => void;
}

interface SupportingEvidenceContentProps {
  facts: SupportingEvidenceProps['facts'];
  answerMessage: SupportingEvidenceProps['answerMessage'];
  animate: SupportingEvidenceProps['animate'];
}

const SupportingEvidenceContent = ({ facts, answerMessage, animate }: SupportingEvidenceContentProps) => {
  if (!answerMessage || answerMessage.facts.length === 0) {
    return null;
  }

  const { openReferenceDrawer } = useStore();

  const referencesByFactId = answerMessage.references
    ? Object.fromEntries(Object.entries(answerMessage.references).map(([key, value]) => [value.id, key]))
    : null;

  return (
    <div className="mb-2">
      <div className="flex flex-col gap-2">
        {facts.map((fact, index) => {
          return (
            <div
              className={`transform rounded-lg border transition-all duration-500 ease-out ${
                animate ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
              key={fact.id}
              id={fact.id}
            >
              <FactDropdown
                fact={fact}
                reference={referencesByFactId?.[fact.id]}
                hideRelevanceIndicator
                compressedVersion
                onViewDocument={() => openReferenceDrawer({ factId: fact.id, caseId: fact.caseId })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SupportingEvidence = ({
  loading,
  confidenceLevel,
  answerMessage,
  facts,
  animate,
  setShowAllChunks,
  showAllChunks,
}: SupportingEvidenceProps) => {
  const hasNoContent =
    !loading &&
    (!answerMessage || ((answerMessage?.facts?.length ?? 0) === 0 && (answerMessage?.chunks?.length ?? 0) === 0));

  if (hasNoContent) {
    return null;
  }

  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const navigate = useNavigate();

  const [selectChronsModalOpen, setSelectChronsModalOpen] = useState(false);

  const { mutate: createChronologyFromThread, isLoading: isCreatingChronology } = useCreateChronologyFromThread();

  return (
    <div className="mt-5">
      <div className="mx-2 mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Book className="text-brandSecondary mb-0.5 size-4.5" />

          <h2 className="text-sm font-semibold text-gray-800">
            {confidenceLevel.text === 'Low' ? 'Possible Information' : 'Supporting Evidence'}
          </h2>
        </div>

        {!loading && caseId && answerMessage && facts.length > 0 && (
          <div className="flex gap-2">
            <Button
              className="relative h-auto border-gray-300 bg-gray-50 px-2.5 py-1.5 hover:border-gray-400"
              variant={'outline'}
              onClick={() =>
                createChronologyFromThread(
                  { caseId, threadId: answerMessage.threadId },
                  {
                    onSuccess: (data) => {
                      const newParams = new URLSearchParams(searchParams);
                      newParams.set('chronologyId', data.chronologyId);
                      navigate('/app/chronos/case-editor/data/chronology/view?' + newParams.toString());
                    },
                  },
                )
              }
              disabled={isCreatingChronology}
            >
              {isCreatingChronology ? (
                <LoaderCircle className="animate-spin text-gray-600" />
              ) : (
                <FontAwesomeIcon icon={faTimeline} size="sm" className="text-gray-600" />
              )}
              Create Chronology from Evidence
            </Button>

            <Button
              className="relative h-auto border-gray-300 bg-gray-50 px-2.5 py-1.5 hover:border-gray-400"
              variant={'outline'}
              onClick={() => setSelectChronsModalOpen(true)}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="text-gray-600" />
              Add Evidence to Existing Chronology
              <NewChip />
            </Button>

            <SelectChronologyModal
              isOpen={selectChronsModalOpen}
              handleClose={() => setSelectChronsModalOpen(false)}
              factIds={facts.map((f) => f.id)}
            />
          </div>
        )}
      </div>

      <SkeletonOrComponent
        isLoading={loading}
        component={<SupportingEvidenceContent facts={facts} answerMessage={answerMessage} animate={animate} />}
        skeletonHeight="h-12"
      />

      {loading || !answerMessage ? null : answerMessage?.chunks &&
        Array.isArray(answerMessage.chunks) &&
        answerMessage.chunks.length > 0 ? (
        <ShowSupportingTexts
          setShowAllChunks={setShowAllChunks}
          showAllChunks={showAllChunks}
          answerMessage={answerMessage}
        />
      ) : null}
    </div>
  );
};

export default SupportingEvidence;
